/**
 * `/career` as a `SurfaceController`.
 *
 * Everything that decides anything still lives where it did: the roster in
 * `career/career-store.ts`, the sortie in `career/career-session.ts`, the
 * campaign rules in `packages/engine`. This class is the part that knows which
 * screen is on, and it is the only place a command reaches for the store —
 * `career-model.ts` stays pure so the flow can be tested without one.
 */

import {
  createCareer,
  formatCareerDate,
  isCareerOver,
  PAINT_COLORS,
  requestTransfer,
  resumeDuty,
  transferOptions,
  type PaintColor,
} from "@red-opus/engine";
import type { SettingsStore } from "../audio/settings.ts";
import { paintPersonalAircraft, previewSubjectFor, takePersonalAircraft, type PreviewSubject } from "../career/career-aircraft.ts";
import { careerCutsceneScript, cutsceneQueueFor, type CareerCutsceneKind } from "../career/career-cutscenes.ts";
import { acknowledgeConsequence, careerFlightPath, careerSortie } from "../career/career-session.ts";
import {
  activePilot,
  browserCareerStore,
  deleteCareer,
  loadCareers,
  newCareerSeed,
  saveCareer,
  setActivePilot,
  type CareerSave,
  type CareerStore,
} from "../career/career-store.ts";
import { writeBackup } from "../career/career-backup.ts";
import { rosterFullDialog } from "./model/career-backup-screen.ts";
import { consequenceDialog } from "./model/consequence-dialog.ts";
import { INITIAL_POSE } from "../career/preview-pose.ts";
import { applyRealismCommand, currentRealism, saveRealism, type RealismPanel } from "../mission/realism-settings.ts";
import { careerCommand, initialCareer, typeName, type CareerModel } from "./model/career-model.ts";
import { aceRows, careerMenuScreen, careerRosterScreen, enlistScreen, rosterRows, topAcesScreen } from "./model/career-screens.ts";
import { ceremonyScreen, MEDAL_SHEET } from "./model/ceremony-screens.ts";
import { consequenceSections } from "../career/career-view.ts";
import { AERODROME_BACKDROP, pilotScreen } from "./model/career-pilot-screens.ts";
import { SIMEND_SHEETS } from "./model/simend-screens.ts";
import type { MenuAction } from "./model/menu-model.ts";
import type { PaintPart } from "./model/paint-screens.ts";
import { realismScreen } from "./model/realism-screen.ts";
import { noticeDialog, type ShellDialog, type ShellScreen } from "./model/shell-screen.ts";
import { ORNAMENT_SHEET } from "./render/board-chrome.ts";
import type { FontId } from "./render/draw-ops.ts";
import { PANEL_SHEET } from "./render/panel-chrome.ts";
import type { CutsceneRequest, SurfaceController } from "./screen-runtime.ts";
import type { FontAtlas } from "./render/text-metrics.ts";

/** The AERODROME entries with no front-end yet, both of them G40's subject. */
const NOTICE_TEXT: Readonly<Record<string, readonly string[]>> = {
  squadron: ["THE SQUADRON INFORMATION", "SCREEN IS NOT FLYING YET."],
  map: ["THE MAP OF THE FRONT", "IS NOT FLYING YET."],
};

class CareerSurface implements SurfaceController {
  readonly assets = {
    images: ["screens/MAINMENU.SCR", ...Object.values(AERODROME_BACKDROP)],
    sprites: [ORNAMENT_SHEET, PANEL_SHEET, MEDAL_SHEET, ...SIMEND_SHEETS],
  };

  private model: CareerModel;
  private readonly opening: RealismPanel;
  private readonly store: CareerStore | undefined;

  constructor(private readonly settings: SettingsStore | undefined) {
    this.store = browserCareerStore();
    const panel = currentRealism(settings);
    this.opening = panel;
    const resumed = this.store ? activePilot(this.store) : undefined;
    this.model = initialCareer(panel, resumed !== undefined);
  }

  state(): CareerModel {
    return this.model;
  }

  saves(): readonly CareerSave[] {
    return this.store ? loadCareers(this.store) : [];
  }

  private active(): CareerSave | undefined {
    return this.store ? activePilot(this.store) : undefined;
  }

  screen(fonts: ReadonlyMap<FontId, FontAtlas>): ShellScreen {
    const font = atlas(fonts, "FONT3");
    const heading = atlas(fonts, "FONT1");
    const view = this.model.view;
    if (view === "realism") return realismScreen({ panel: this.model.realism });
    if (view === "roster") return careerRosterScreen(this.model, rosterRows(this.saves()), heading);
    if (view === "topAces") return topAcesScreen(this.model, aceRows(this.saves()), heading);
    if (view === "enlist") return enlistScreen(this.model, font, heading);
    const save = this.active();
    // `"menu"` is honoured even while a pilot is active. Until B5 this test was
    // `!save` alone, so an active pilot shadowed the one screen START A CAREER
    // lives on and no second career could ever be begun.
    if (!save || view === "menu") return careerMenuScreen(this.saves().length > 0);
    const ceremony = this.ceremony(save, font, heading);
    if (ceremony) return ceremony;
    const sortie = this.store ? careerSortie(this.store, save.career.id, this.model.realism) : undefined;
    return pilotScreen({ model: this.model, save, fonts, spec: sortie?.assignment.spec });
  }

  /** What the turntable should be showing, or `undefined` when no screen has one. */
  previewSubject(): PreviewSubject | undefined {
    return previewSubjectFor(this.active(), this.model.view === "aircraft", this.model.preview);
  }

  dialog(): ShellDialog | undefined {
    if (this.model.dialog) return this.model.dialog;
    const derived = this.consequenceDialog();
    return derived ? { ...derived, focus: this.model.dialogFocus } : undefined;
  }

  /** The consequences of the sortie just flown, if that is what is on screen. */
  private consequenceDialog(): ShellDialog | undefined {
    const save = this.active();
    if (this.model.view !== "consequences" || !save?.lastConsequence) return undefined;
    return consequenceDialog(save.lastConsequence, this.model.consequence);
  }

  /**
   * The medal citation, the promotion card and the Board of Inquiry are screens
   * in the original, not popups, so they replace the screen underneath for the
   * one consequence step that is theirs. Their CONTINUE carries the *same*
   * action the popup's would have — `next-consequence` or `acknowledge` — so the
   * step machine in `career-model.ts` does not know they exist.
   */
  private ceremony(save: CareerSave, font: FontAtlas, heading: FontAtlas): ShellScreen | undefined {
    if (this.model.view !== "consequences" || !save.lastConsequence) return undefined;
    const sections = consequenceSections(save.lastConsequence);
    const section = sections[this.model.consequence];
    if (!section) return undefined;
    const last = this.model.consequence >= sections.length - 1;
    const action: MenuAction = { kind: "command", command: last ? "acknowledge" : "next-consequence" };
    return ceremonyScreen(section, save.career.nation, font, heading, action);
  }

  focus(): number {
    return this.model.focus;
  }

  setFocus(index: number): void {
    this.model = { ...this.model, focus: index };
  }

  /**
   * The runtime moves a dialog's cursor by handing back a modified copy of
   * whatever `dialog()` last returned — on every hover, click and arrow key
   * (`screen-runtime.ts`'s `pointerMove`, `pointerDown`, `step`). For an
   * *owned* dialog that copy is the state. The consequences popup is derived,
   * rebuilt every paint, and storing the copy froze it on page one forever:
   * that was B5, written up in `lessons.md`. So a derived dialog contributes
   * its cursor and nothing else.
   */
  setDialog(dialog: ShellDialog | undefined): void {
    if (dialog && !this.model.dialog) {
      this.model = { ...this.model, dialogFocus: dialog.focus };
      return;
    }
    this.model = { ...this.model, dialog };
  }

  typeKey(key: string): boolean {
    const next = typeName(this.model, key);
    if (next === this.model) return false;
    this.model = next;
    return true;
  }

  /** The commands that need the store. Everything else falls through to the pure model. */
  private storeCommand(name: string): string | undefined | false {
    const store = this.store;
    if (!store) return false;
    if (name === "enlist") return this.enlist(store);
    if (name === "select") return this.select(store);
    if (name === "delete") return this.remove(store);
    if (name === "resume") return this.resume(store);
    if (name === "begin") return this.begin();
    if (name === "acknowledge") return this.acknowledge(store);
    if (name === "backup-save") return this.backup(store);
    if (name === "take-aircraft") return this.takeAircraft(store);
    if (name === "accept-transfer") return this.acceptTransfer(store);
    if (name.startsWith("color:")) return this.paint(store, name.slice(6) as PaintColor);
    if (name.startsWith("part:")) {
      this.model = { ...this.model, paintPart: name.slice(5) as PaintPart, view: "paintColour", focus: 0 };
      return undefined;
    }
    return false;
  }

  /** REQUEST NEW AIRCRAFT → the list → the machine is his. */
  private takeAircraft(store: CareerStore): undefined {
    const save = this.active();
    if (!save || !takePersonalAircraft(store, save, this.model.listIndex)) return undefined;
    this.model = { ...this.model, view: "aircraft", focus: 0, preview: INITIAL_POSE };
    return undefined;
  }

  /**
   * `Pick a Squadron:` answered with ACCEPT.
   *
   * The gate is asked for twice on purpose. `transferScreen` disables the button
   * on an ineligible row, and `requestTransfer` throws the squadron's refusal
   * anyway — but a list row is also a click target in its own right, so a
   * double-click on a refusing squadron reaches here with the button greyed.
   * Re-reading `eligible` is what keeps the throw for genuine races and not for
   * the ordinary case.
   */
  private acceptTransfer(store: CareerStore): undefined {
    const save = this.active();
    if (!save) return undefined;
    const chosen = transferOptions(save.career)[this.model.listIndex];
    if (!chosen?.eligible) return undefined;
    saveCareer(store, requestTransfer(save.career, chosen.squadron.id));
    this.model = { ...this.model, view: "aerodrome", focus: 0, listIndex: 0, listTop: 0 };
    return undefined;
  }

  /** `WHICH COLOR?` answered — see `career-aircraft.ts` for what `everything` means. */
  private paint(store: CareerStore, color: PaintColor): undefined {
    const save = this.active();
    if (!save || !PAINT_COLORS.includes(color)) return undefined;
    paintPersonalAircraft(store, save, this.model.paintPart, color);
    this.model = { ...this.model, view: "aircraft", focus: 0 };
    return undefined;
  }

  /**
   * BACKUP CAREER confirmed. The copy is a *pilot*, not a slot — it lands on
   * the roster under its new name and CONTINUE A CAREER is the restore path,
   * exactly as the manual describes. `career-backup.ts` decides the outcome;
   * this only says what the player sees of it.
   */
  private backup(store: CareerStore): undefined {
    const save = this.active();
    if (!save) return undefined;
    const outcome = writeBackup(store, save, this.model.name);
    if (outcome.kind === "noName") return undefined;
    if (outcome.kind === "rosterFull") {
      this.model = { ...this.model, dialog: rosterFullDialog() };
      return undefined;
    }
    if (outcome.kind === "sameName") {
      this.model = { ...this.model, dialog: noticeDialog("BACKUP CAREER", ["THAT IS THIS PILOT'S OWN NAME."]) };
      return undefined;
    }
    this.model = { ...this.model, view: "aerodrome", focus: 0, name: "", dialog: undefined };
    return undefined;
  }

  private enlist(store: CareerStore): undefined {
    const name = this.model.name.trim();
    if (name === "") return undefined;
    const career = createCareer({ name, nation: this.model.nation, period: this.model.period }, newCareerSeed());
    saveCareer(store, career);
    setActivePilot(store, career.id);
    this.model = { ...this.model, view: "aerodrome", focus: 0, dialog: undefined };
    return undefined;
  }

  private select(store: CareerStore): undefined {
    const save = this.saves()[this.model.listIndex];
    if (!save) return undefined;
    setActivePilot(store, save.career.id);
    this.model = { ...this.model, view: "aerodrome", focus: 0, dialog: undefined };
    return undefined;
  }

  private remove(store: CareerStore): undefined {
    const save = this.saves()[this.model.listIndex];
    if (save) deleteCareer(store, save.career.id);
    this.model = { ...this.model, listIndex: 0, listTop: 0 };
    return undefined;
  }

  private resume(store: CareerStore): undefined {
    const save = this.active();
    if (save) saveCareer(store, resumeDuty(save.career));
    return undefined;
  }

  private begin(): string | undefined {
    const save = this.active();
    return save ? careerFlightPath(save.career.id) : undefined;
  }

  /**
   * The last page of the consequences, read. A career that has *ended* has no
   * aerodrome to go back to — every entry on that board is greyed — so the
   * fallen pilot is released and the flow lands on the CAREER MENU, where START
   * A CAREER is. His save is untouched: CONTINUE A CAREER still opens him.
   * Releasing him is also what makes a *reload* land on the menu (B5).
   */
  private acknowledge(store: CareerStore): undefined {
    const save = this.active();
    if (save) acknowledgeConsequence(store, save.career.id);
    const over = save !== undefined && isCareerOver(save.career);
    if (over) setActivePilot(store, undefined);
    this.model = {
      ...this.model,
      view: over ? "menu" : "aerodrome",
      focus: 0,
      listIndex: 0,
      listTop: 0,
      dialog: undefined,
      dialogFocus: 0,
      cutsceneQueue: [],
    };
    return undefined;
  }

  /**
   * The milestone scenes this sortie's consequence earned, front-loaded onto
   * `"consequences"` so they play before any of the citation dialog's own
   * text does — see `CareerModel.cutsceneQueue`'s own note. A pure
   * `careerCommand("consequences")` (exercised directly by
   * `shell-flow.test.ts` with no store at all) still works exactly as
   * before; this only adds the queue on top when a save is actually active.
   */
  private consequencesQueue(): readonly CareerCutsceneKind[] {
    const save = this.active();
    return save?.lastConsequence ? cutsceneQueueFor(save.lastConsequence) : [];
  }

  run(action: MenuAction | undefined): string | undefined {
    if (!action) return undefined;
    if (action.kind === "navigate") return action.path;
    if (action.kind === "close") {
      this.model = { ...this.model, dialog: undefined };
      return undefined;
    }
    if (action.kind !== "command") return undefined;
    if (action.command.startsWith("notice:")) {
      const key = action.command.slice(7);
      this.model = { ...this.model, dialog: noticeDialog(key.toUpperCase(), NOTICE_TEXT[key] ?? []) };
      return undefined;
    }
    const panel = applyRealismCommand(this.model.realism, action.command, this.opening);
    if (panel) {
      // Closes the `START POSITION` dialog too — see `missions-surface.ts`'s
      // own copy of this comment.
      this.model = { ...this.model, realism: panel, dialog: undefined };
      if (this.settings) saveRealism(this.settings, panel);
      return undefined;
    }
    const handled = this.storeCommand(action.command);
    if (handled !== false) return handled;
    const next = careerCommand(this.model, action.command);
    this.model =
      action.command === "consequences" ? { ...next.model, cutsceneQueue: this.consequencesQueue() } : next.model;
    return next.effect?.kind === "navigate" ? next.effect.path : undefined;
  }

  /** The next queued milestone scene, or `undefined` once the queue is spent — see `screen-runtime.ts`'s `SurfaceController.cutscene`. */
  cutscene(): CutsceneRequest | undefined {
    const kind = this.model.cutsceneQueue[0];
    const save = this.active();
    if (!kind || !save) return undefined;
    const script = careerCutsceneScript(kind, save.career.nation);
    return { id: `${kind}:${save.career.nation}`, ttm: script.ttm, song: script.song };
  }

  cutsceneFinished(): void {
    this.model = { ...this.model, cutsceneQueue: this.model.cutsceneQueue.slice(1) };
  }

  /** Coming back from `/fly`: the sortie just flown decides whether SAFE RETURN or the debriefing is first. */
  openConsequences(): void {
    const save = this.active();
    if (!save?.lastConsequence) return;
    const survived = save.career.status === "active";
    this.model = { ...this.model, view: survived ? "safeReturn" : "debrief", focus: 0 };
  }

  /** For the roster date column, which the original shows beside the pilot's name. */
  static rosterDate(save: CareerSave): string {
    return formatCareerDate(save.career.date);
  }
}

function atlas(fonts: ReadonlyMap<FontId, FontAtlas>, id: FontId): FontAtlas {
  const found = fonts.get(id);
  if (!found) throw new Error(`shell: font atlas ${id} was never loaded`);
  return found;
}

export function createCareerSurface(settings: SettingsStore | undefined): CareerSurface {
  return new CareerSurface(settings);
}

export type { CareerSurface };
